import {useMemo} from 'react';

import ModelTypeConstants from 'constants/ModelTypeConstants';
import type {TagSchema} from 'constants/RegistryConstants';
import {QueryResultType, useSkippableBenchlingQuery} from 'graphql-client/BenchlingQuery';
import {graphQLArrayToREST} from 'graphql-client/declarative-translations/external/translate';
import {getRequiredLinkTagSchema} from 'schemas/fields/SchemaFieldConstants';
import type {TagSchemaSelectorQuery} from 'schemas/shared/__generated/TagSchemaSelectorQuery';
import {TAG_SCHEMA_SELECTOR_QUERY} from 'schemas/shared/TagSchemaOrInterfaceSelector';

export interface SchemaPairing {
  formSchemaId: string;
  formSchemaName: string;
  compoundSchemaId: string;
  compoundSchemaName: string;
}

export interface UseMoleculeSchemasPairingResult {
  pairingMap: Record<string, SchemaPairing>;
  schemasById: Record<string, TagSchema>;
  isLoading: boolean;
}

/**
 * Given an array of TagSchema objects (REST format), builds a bidirectional
 * pairing map from form schemas to their parent (compound) schemas and vice versa.
 */
export function buildPairingMap(moleculeSchemas: Array<TagSchema>): Record<string, SchemaPairing> {
  const map: Record<string, SchemaPairing> = {};
  for (const schema of moleculeSchemas) {
    const parentLinkField = schema.fields?.find((field) => field.isParentLink);
    if (!parentLinkField) {
      continue;
    }

    const targetSchema = getRequiredLinkTagSchema(parentLinkField);
    if (!targetSchema) {
      continue;
    }

    const pairing: SchemaPairing = {
      formSchemaId: schema.id,
      formSchemaName: schema.name,
      compoundSchemaId: targetSchema.id,
      compoundSchemaName: targetSchema.name,
    };
    map[schema.id] = pairing;
    map[targetSchema.id] = pairing;
  }
  return map;
}

export default function useMoleculeSchemasPairing(
  registryId: string | null
): UseMoleculeSchemasPairingResult {
  const queryResponse = useSkippableBenchlingQuery<TagSchemaSelectorQuery>(TAG_SCHEMA_SELECTOR_QUERY);

  const moleculeSchemas: Array<TagSchema> = useMemo(() => {
    if (queryResponse.type !== QueryResultType.SUCCESS) {
      return [];
    }
    const allSchemas: Array<TagSchema> = graphQLArrayToREST('TagSchema', queryResponse.data.tagSchemas);
    return allSchemas.filter((schema) => {
      if (schema.folderItemType !== ModelTypeConstants.MOLECULE) {
        return false;
      }
      if (registryId != null && schema.registryId !== registryId) {
        return false;
      }
      return true;
    });
  }, [queryResponse, registryId]);

  const pairingMap = useMemo(() => buildPairingMap(moleculeSchemas), [moleculeSchemas]);

  const schemasById: Record<string, TagSchema> = useMemo(() => {
    const map: Record<string, TagSchema> = {};
    for (const schema of moleculeSchemas) {
      map[schema.id] = schema;
    }
    return map;
  }, [moleculeSchemas]);

  const isLoading = queryResponse.type === QueryResultType.PENDING;

  return {pairingMap, schemasById, isLoading};
}
